import { type CustomerAgeingData } from "@/types/ageing-analysis";
import { type Transaction } from "@/types/transaction";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Search, Settings, ArrowUpDown, ArrowUp, ArrowDown, X, FileSpreadsheet, Mail, ChevronDown, Briefcase, HandCoins, AlertTriangle, StickyNote, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { useState, useMemo, useEffect } from "react";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration";
import { toast } from "sonner";
import { TransactionDetailModal } from "./transaction-detail-modal";
import { RecordPromiseDialog, type RecordPromiseData } from "./record-promise-dialog";
import { CreateCaseModal } from "./create-case-modal";
import { useCaseContacts } from "@/hooks/useCaseDetailData";
import type { ContactUI } from "@/types/ui/customer-detail";
import { AddNoteModal, type NoteFormData } from "@/components/transactions/transaction-action-modals";

interface TransactionsTabProps {
  customer: CustomerAgeingData;
  formatCurrency: (amount: number) => string;
}

const DEFAULT_OUTSTANDING_TRANSACTIONS_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference #', visible: true, order: 0 },
  { id: 'description', label: 'Description', visible: true, order: 1 },
  { id: 'flags', label: 'Flags', visible: true, order: 2 },
  { id: 'date', label: 'Date', visible: true, order: 3 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 4 },
  { id: 'amount', label: 'Amount', visible: true, order: 5 },
  { id: 'outstanding', label: 'Outstanding', visible: true, order: 6 },
  { id: 'daysOverdue', label: 'Days Overdue', visible: true, order: 7 },
];

export function TransactionsTab({ customer, formatCurrency }: Readonly<TransactionsTabProps>) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false);
  
  // Transaction detail modal state
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  
  // Record Promise modal state
  const [isRecordPromiseModalOpen, setIsRecordPromiseModalOpen] = useState(false);
  
  // Create Collection Case modal state
  const [isCreateCaseModalOpen, setIsCreateCaseModalOpen] = useState(false);
  
  // Add Note modal state
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  
  // Fetch contacts for the customer
  const { data: contactsData = [] } = useCaseContacts(customer.id);
  const [contacts, setContacts] = useState<ContactUI[]>([]);
  
  // Sync contacts data
  useEffect(() => {
    if (contactsData.length > 0) {
      setContacts(contactsData);
    }
  }, [contactsData]);
  
  // Column preferences management
  const {
    columns: outstandingColumns,
    visibleColumns: visibleOutstandingColumns,
    savePreferences: saveOutstandingPreferences,
    resetToDefaults: resetOutstandingPreferences,
  } = useScreenColumnPreferences(
    UniqueTableNamesForColumnConfiguration.AgeingAnalysisCustomerOutstandingTransactions,
    DEFAULT_OUTSTANDING_TRANSACTIONS_COLUMNS
  );

  // Sorting state
  type SortField = "referenceNumber" | "date" | "dueDate" | "amount" | "outstanding" | "daysOverdue" | "ageing";
  type SortDirection = "asc" | "desc" | null;
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  
  // Ageing bucket filter state
  const [selectedAgeingBucket, setSelectedAgeingBucket] = useState<string | null>(null);

  // Selection state
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<Set<string>>(new Set());

  // Toggle ageing bucket filter
  const handleAgeingBucketClick = (bucket: string) => {
    setSelectedAgeingBucket(prev => prev === bucket ? null : bucket);
  };

  // Clear ageing bucket filter
  const clearAgeingFilter = () => {
    setSelectedAgeingBucket(null);
  };

  // Selection handlers
  const selectAll = () => {
    setSelectedTransactionIds(new Set(filteredInvoices.map(inv => inv.id)));
  };

  const deselectAll = () => {
    setSelectedTransactionIds(new Set());
  };

  const handleSelectTransaction = (transactionId: string, checked: boolean) => {
    const newSelection = new Set(selectedTransactionIds);
    if (checked) {
      newSelection.add(transactionId);
    } else {
      newSelection.delete(transactionId);
    }
    setSelectedTransactionIds(newSelection);
  };

  const handleClearSelection = () => {
    setSelectedTransactionIds(new Set());
  };

  // Action handlers
  const handleExportSelected = () => {
    toast.success(`Exporting ${selectedTransactionIds.size} selected transactions...`);
  };

  const handleSendCopyInvoice = () => {
    toast.success("Opening Send Copy Invoice dialog...");
  };

  const handleCreateCollectionCase = () => {
    setIsCreateCaseModalOpen(true);
  };

  const handleCreatePromise = () => {
    setIsRecordPromiseModalOpen(true);
  };

  const handleSavePromise = (promiseData: RecordPromiseData) => {
    console.log("Saving promise:", promiseData);
    toast.success("Promise to pay recorded successfully");
    setIsRecordPromiseModalOpen(false);
  };

  const handleCaseCreated = () => {
    console.log("Collection case created successfully");
    setIsCreateCaseModalOpen(false);
  };

  const handleCreateDispute = () => {
    toast.success("Opening Flag as Disputed dialog...");
  };

  const handleAddNote = () => {
    setIsAddNoteModalOpen(true);
  };

  const handleSaveNote = (noteData: NoteFormData) => {
    console.log("Saving note:", noteData);
    toast.success("Note added successfully");
    setIsAddNoteModalOpen(false);
    // Clear selection after adding note
    setSelectedTransactionIds(new Set());
  };

  // Convert OutstandingInvoice to Transaction for the modal
  const convertInvoiceToTransaction = (invoice: typeof outstandingInvoices[0]): Transaction => {
    return {
      id: parseInt(invoice.id) || 0,
      referenceNumber: invoice.invoiceNumber,
      transactionDate: invoice.date,
      customerId: parseInt(customer.id) || 0,
      customerName: customer.customerName,
      transactionType: "invoice",
      amount: invoice.amount,
      currency: customer.currency || "GBP",
      status: invoice.daysOverdue > 0 ? "overdue" : "outstanding",
      description: invoice.description || `Invoice ${invoice.invoiceNumber}`,
      createdBy: "System",
      createdAt: invoice.date,
      dueDate: invoice.dueDate,
      outstandingAmount: invoice.outstanding,
      onDispute: invoice.flags?.isDisputed,
    };
  };

  // Handle opening transaction detail modal
  const handleOpenTransactionDetail = (invoice: typeof outstandingInvoices[0]) => {
    const transaction = convertInvoiceToTransaction(invoice);
    setSelectedTransaction(transaction);
    setIsTransactionModalOpen(true);
  };


  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null (no sort)
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="h-4 w-4" />;
    }
    return <ArrowDown className="h-4 w-4" />;
  };

  const getAgeingBucketLabel = (bucket: string) => {
    switch (bucket) {
      case "current":
        return "Current (0-30)";
      case "days31_60":
        return "31-60 Days";
      case "days61_90":
        return "61-90 Days";
      case "days91_120":
        return "91-120 Days";
      case "over120":
        return "Over 120 Days";
      default:
        return bucket;
    }
  };

  const getAgeingBucketColor = (bucket: string) => {
    switch (bucket) {
      case "current":
        return "bg-green-100 text-green-800 border-green-200";
      case "days31_60":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "days61_90":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "days91_120":
        return "bg-red-100 text-red-800 border-red-200";
      case "over120":
        return "bg-red-100 text-red-900 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getBucketOverdueDays = (bucket: string): number => {
    switch (bucket) {
      case "current": return 0;
      case "days31_60": return 45;
      case "days61_90": return 75;
      case "days91_120": return 105;
      default: return 150;
    }
  };

  const getOverdueColor = (daysOverdue: number) => {
    if (daysOverdue <= 0) return "text-green-600";
    if (daysOverdue <= 30) return "text-green-600";
    if (daysOverdue <= 60) return "text-yellow-600";
    if (daysOverdue <= 90) return "text-orange-600";
    if (daysOverdue <= 120) return "text-red-600";
    return "text-red-700";
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "dd MMM yyyy");
    } catch {
      return dateString;
    }
  };

  const outstandingInvoices = customer.outstandingInvoices || [];

  // Filter and sort invoices based on search query, ageing bucket filter, and sort settings
  const filteredInvoices = useMemo(() => {
    let result = outstandingInvoices.filter((invoice) => {
      // Apply search filter
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        invoice.invoiceNumber.toLowerCase().includes(query) ||
        invoice.amount.toString().includes(query) ||
        invoice.outstanding.toString().includes(query) ||
        formatDate(invoice.date).toLowerCase().includes(query) ||
        formatDate(invoice.dueDate).toLowerCase().includes(query)
      );
      
      // Apply ageing bucket filter
      const matchesAgeingFilter = !selectedAgeingBucket || invoice.ageingBucket === selectedAgeingBucket;
      
      return matchesSearch && matchesAgeingFilter;
    });

    // Apply sorting if a sort field is selected
    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortField) {
          case "referenceNumber":
            aValue = a.invoiceNumber.toLowerCase();
            bValue = b.invoiceNumber.toLowerCase();
            break;
          case "date":
            aValue = new Date(a.date).getTime();
            bValue = new Date(b.date).getTime();
            break;
          case "dueDate":
            aValue = new Date(a.dueDate).getTime();
            bValue = new Date(b.dueDate).getTime();
            break;
          case "amount":
            aValue = a.amount;
            bValue = b.amount;
            break;
          case "outstanding":
            aValue = a.outstanding;
            bValue = b.outstanding;
            break;
          case "daysOverdue":
            aValue = a.daysOverdue;
            bValue = b.daysOverdue;
            break;
          case "ageing": {
            // Sort by ageing bucket order
            const ageingOrder = ["current", "days31_60", "days61_90", "days91_120", "over120"];
            aValue = ageingOrder.indexOf(a.ageingBucket);
            bValue = ageingOrder.indexOf(b.ageingBucket);
            break;
          }
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [outstandingInvoices, searchQuery, selectedAgeingBucket, sortField, sortDirection]);

  const totalOutstanding = filteredInvoices.reduce((sum, invoice) => sum + invoice.outstanding, 0);

  // Calculate selected transactions totals
  const selectedTransactions = useMemo(() => {
    return filteredInvoices.filter(inv => selectedTransactionIds.has(inv.id));
  }, [filteredInvoices, selectedTransactionIds]);

  const selectedTotals = useMemo(() => {
    const totalAmount = selectedTransactions.reduce((sum, inv) => sum + inv.amount, 0);
    const totalOutstanding = selectedTransactions.reduce((sum, inv) => sum + inv.outstanding, 0);
    return {
      totalAmount,
      totalOutstanding,
      currency: customer.currency || "GBP",
    };
  }, [selectedTransactions, customer.currency]);

  // Check if all filtered invoices are selected
  const allSelected = filteredInvoices.length > 0 && selectedTransactionIds.size === filteredInvoices.length;
  const someSelected = selectedTransactionIds.size > 0 && selectedTransactionIds.size < filteredInvoices.length;

  return (
    <div className="relative h-full">
      <ScrollArea className="h-full">
        <div className="space-y-6">
          {/* Transactions Table */}
          <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="text-base">Outstanding Transactions</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative w-64">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search transactions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  title="Configure Columns"
                  onClick={() => setIsColumnConfigOpen(true)}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            {/* Filter Summary */}
            {selectedAgeingBucket && (
              <div className="mb-4 flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Filtered by:</span>
                <Badge variant="secondary" className="gap-2">
                  {getAgeingBucketLabel(selectedAgeingBucket)}
                  <button
                    onClick={clearAgeingFilter}
                    className="ml-1 hover:text-foreground"
                    aria-label="Clear filter"
                  >
                    ×
                  </button>
                </Badge>
                <span className="text-sm text-muted-foreground">
                  ({filteredInvoices.length} {filteredInvoices.length === 1 ? "transaction" : "transactions"})
                </span>
              </div>
            )}
            {filteredInvoices.length > 0 ? (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={(checked) => { checked ? selectAll() : deselectAll(); }}
                          aria-label="Select all transactions"
                          className={cn(
                            someSelected && "data-[state=checked]:bg-primary/50"
                          )}
                        />
                      </TableHead>
                      {visibleOutstandingColumns.map((column) => {
                        const isSortable = [
                          "referenceNumber", "date", "dueDate", "amount", 
                          "outstanding", "daysOverdue", "ageing"
                        ].includes(column.id);
                        
                        return (
                          <TableHead 
                            key={column.id}
                            className={cn(
                              column.id === 'amount' || column.id === 'outstanding' ? 'text-right' : ''
                            )}
                          >
                            {isSortable ? (
                              <button
                                onClick={() => handleSort(column.id as SortField)}
                                className="flex items-center gap-1 hover:text-foreground transition-colors font-medium -ml-3 pl-3 pr-2 py-1 rounded hover:bg-muted/50"
                              >
                                <span>{column.label}</span>
                                <span className={sortField === column.id ? "text-foreground" : "text-muted-foreground/40"}>
                                  {getSortIcon(column.id as SortField)}
                                </span>
                              </button>
                            ) : (
                              column.label
                            )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedTransactionIds.has(invoice.id)}
                            onCheckedChange={(checked) => handleSelectTransaction(invoice.id, checked as boolean)}
                            aria-label={`Select transaction ${invoice.invoiceNumber}`}
                          />
                        </TableCell>
                        {visibleOutstandingColumns.map((column) => {
                          switch (column.id) {
                            case 'referenceNumber':
                              return (
                                <TableCell key={column.id} className="font-medium">
                                  <button
                                    onClick={() => handleOpenTransactionDetail(invoice)}
                                    className="text-primary hover:underline transition-colors text-left"
                                    title={`View details for ${invoice.invoiceNumber}`}
                                  >
                                    {invoice.invoiceNumber}
                                  </button>
                                </TableCell>
                              );
                            case 'description':
                              return (
                                <TableCell key={column.id}>
                                  {invoice.description || '-'}
                                </TableCell>
                              );
                            case 'flags':
                              return (
                                <TableCell key={column.id}>
                                  <TooltipProvider>
                                    <div className="flex items-center gap-2">
                                      {invoice.flags?.isDisputed && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 transition-colors cursor-help">
                                              <AlertTriangle className="h-4 w-4 text-red-600" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top" className="bg-red-600 text-white border-red-700">
                                            <p className="font-semibold">Disputed</p>
                                            <p className="text-xs">This transaction is under dispute</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                      {invoice.flags?.hasPromiseToPay && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors cursor-help">
                                              <HandCoins className="h-4 w-4 text-blue-600" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top" className="bg-blue-600 text-white border-blue-700">
                                            <p className="font-semibold">Promise to Pay</p>
                                            <p className="text-xs">Customer has committed to payment</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                      {invoice.flags?.isInCollectionCase && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 hover:bg-orange-200 transition-colors cursor-help">
                                              <Briefcase className="h-4 w-4 text-orange-600" />
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent side="top" className="bg-orange-600 text-white border-orange-700">
                                            <p className="font-semibold">Collection Case</p>
                                            <p className="text-xs">Part of an active collection case</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                      {!invoice.flags?.isDisputed && !invoice.flags?.hasPromiseToPay && !invoice.flags?.isInCollectionCase && (
                                        <span className="text-muted-foreground text-xs">-</span>
                                      )}
                                    </div>
                                  </TooltipProvider>
                                </TableCell>
                              );
                            case 'date':
                              return (
                                <TableCell key={column.id}>
                                  {formatDate(invoice.date)}
                                </TableCell>
                              );
                            case 'dueDate':
                              return (
                                <TableCell key={column.id}>
                                  {formatDate(invoice.dueDate)}
                                </TableCell>
                              );
                            case 'amount':
                              return (
                                <TableCell key={column.id} className="text-right font-medium">
                                  {formatCurrency(invoice.amount)}
                                </TableCell>
                              );
                            case 'outstanding':
                              return (
                                <TableCell key={column.id} className="text-right font-semibold">
                                  {formatCurrency(invoice.outstanding)}
                                </TableCell>
                              );
                            case 'daysOverdue':
                              return (
                                <TableCell key={column.id}>
                                  <span className={cn("font-semibold", getOverdueColor(invoice.daysOverdue))}>
                                    {invoice.daysOverdue > 0 ? `${invoice.daysOverdue} days` : "Current"}
                                  </span>
                                </TableCell>
                              );
                            default:
                              return null;
                          }
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="border-t bg-muted/50 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">Outstanding Total</span>
                    <span className="text-lg font-bold">{formatCurrency(totalOutstanding)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-20" />
                {outstandingInvoices.length === 0 ? (
                  <>
                    <p className="text-sm font-medium">No Outstanding Transactions</p>
                    <p className="text-xs mt-1">This customer has no outstanding invoices</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium">No Matching Transactions</p>
                    <p className="text-xs mt-1">Try adjusting your search query</p>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ageing Distribution */}
        {outstandingInvoices.length > 0 && (
          <Card>
            <CardHeader className="pb-2 pt-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Ageing Distribution</CardTitle>
                {selectedAgeingBucket && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAgeingFilter}
                    className="h-6 text-xs px-2"
                  >
                    Clear Filter
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-2 pb-4">
              <div className="space-y-1.5">
                {Object.entries(customer.ageing).map(([bucket, amount]) => {
                  if (amount === 0) return null;
                  const invoiceCount = outstandingInvoices.filter(
                    inv => inv.ageingBucket === bucket
                  ).length;
                  const isSelected = selectedAgeingBucket === bucket;
                  const totalAmount = Object.values(customer.ageing).reduce((sum, val) => sum + val, 0);
                  const percentage = totalAmount > 0 ? (amount / totalAmount) * 100 : 0;
                  
                  return (
                    <button
                      key={bucket}
                      onClick={() => handleAgeingBucketClick(bucket)}
                      className={cn(
                        "w-full rounded-lg border p-2 transition-all cursor-pointer text-left",
                        "hover:bg-muted/50 hover:border-primary/50 hover:shadow-sm",
                        isSelected && "bg-muted border-primary shadow-sm ring-1 ring-primary/20"
                      )}
                    >
                      <div className="flex items-center gap-2.5">
                        {/* Left: Badge and invoice count */}
                        <div className="flex items-center gap-1.5 min-w-[180px]">
                          <Badge className={cn("text-xs py-0 h-5", getAgeingBucketColor(bucket))}>
                            {getAgeingBucketLabel(bucket)}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            ({invoiceCount} {invoiceCount === 1 ? "invoice" : "invoices"})
                          </span>
                        </div>
                        
                        {/* Center: Horizontal bar chart */}
                        <div className="flex-1 relative h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={cn(
                              "absolute inset-y-0 left-0 rounded-full transition-all duration-500",
                              bucket === "current" && "bg-green-500",
                              bucket === "days31_60" && "bg-yellow-500",
                              bucket === "days61_90" && "bg-orange-500",
                              bucket === "days91_120" && "bg-red-500",
                              bucket === "over120" && "bg-red-700"
                            )}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        
                        {/* Right: Amount and percentage */}
                        <div className="flex items-center gap-2 min-w-[120px] justify-end">
                          <span className={cn("text-sm font-semibold", getOverdueColor(getBucketOverdueDays(bucket)))}>
                            {formatCurrency(amount)}
                          </span>
                          <span className="text-xs text-muted-foreground min-w-[38px] text-right">
                            {percentage.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
        </div>
      </ScrollArea>

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={isColumnConfigOpen}
        onOpenChange={setIsColumnConfigOpen}
        columns={outstandingColumns}
        onApply={saveOutstandingPreferences}
        onReset={resetOutstandingPreferences}
      />

      {/* Transaction Detail Modal */}
      <TransactionDetailModal
        open={isTransactionModalOpen}
        onOpenChange={setIsTransactionModalOpen}
        transaction={selectedTransaction}
      />

      {/* Record Promise Modal */}
      <RecordPromiseDialog
        open={isRecordPromiseModalOpen}
        onOpenChange={setIsRecordPromiseModalOpen}
        customerName={customer.customerName}
        totalAmount={selectedTotals.totalOutstanding}
        contacts={contacts}
        onSave={handleSavePromise}
      />

      {/* Create Collection Case Modal */}
      <CreateCaseModal
        open={isCreateCaseModalOpen}
        onOpenChange={setIsCreateCaseModalOpen}
        onCaseCreated={handleCaseCreated}
        prePopulatedCustomer={{
          customerId: customer.id,
          customerName: customer.customerName,
          accountNumber: customer.accountNumber,
        }}
        selectedTransactions={selectedTransactions.map(invoice => convertInvoiceToTransaction(invoice))}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        open={isAddNoteModalOpen}
        onOpenChange={setIsAddNoteModalOpen}
        selectedTransactions={selectedTransactions.map(invoice => convertInvoiceToTransaction(invoice))}
        onSave={handleSaveNote}
      />

      {/* Floating Action Panel - Only show when transactions are selected */}
      {selectedTransactionIds.size > 0 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                      {selectedTransactionIds.size}
                    </Badge>
                    <span className="font-medium">
                      {selectedTransactionIds.size === 1 ? "transaction" : "transactions"} selected
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-primary-foreground/80">
                    <span>
                      Total: <span className="font-semibold">{formatCurrency(selectedTotals.totalAmount)}</span>
                    </span>
                    {selectedTotals.totalOutstanding > 0 && (
                      <>
                        <span>•</span>
                        <span>
                          Outstanding: <span className="font-semibold">{formatCurrency(selectedTotals.totalOutstanding)}</span>
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleExportSelected}
                  className="gap-2"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Export Selected
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" className="gap-2">
                      Actions
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {/* Collection Actions */}
                    <DropdownMenuItem onClick={handleCreateCollectionCase} className="gap-2">
                      <Briefcase className="h-4 w-4" />
                      Create Collection Case
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleCreatePromise} className="gap-2">
                      <HandCoins className="h-4 w-4" />
                      Create Promise to Pay
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleCreateDispute} className="gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Flag as Disputed
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleAddNote} className="gap-2">
                      <StickyNote className="h-4 w-4" />
                      Add Note
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />

                    {/* Communication Actions */}
                    <DropdownMenuItem onClick={handleSendCopyInvoice} className="gap-2">
                      <Mail className="h-4 w-4" />
                      Send Copy Invoice
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
